import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { loadContent, ProductContent } from '../utils/contentLoader';
import PageHeader from '../components/PageHeader';
import './ProductsPage.css';

const ProductsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState<ProductContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [allTags, setAllTags] = useState<string[]>([]);

  // Parse the query parameter on component mount
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tagParam = queryParams.get('tag');
    
    if (tagParam) {
      setActiveTag(tagParam);
    }
  }, [location.search]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productData = await loadContent('products') as ProductContent[];
        setProducts(productData);
        
        // Extract all unique tags
        const tags = new Set<string>();
        productData.forEach(product => {
          product.tags.forEach(tag => tags.add(tag));
        });
        
        setAllTags(Array.from(tags));
        setLoading(false);
      } catch (error) {
        console.error('Error loading products:', error);
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const filteredProducts = activeTag 
    ? products.filter(product => product.tags.includes(activeTag))
    : products;

  // Function to handle tag selection and update URL
  const handleTagSelection = (tag: string | null) => {
    setActiveTag(tag);
    
    // Update URL query parameter
    if (tag) {
      const queryParams = new URLSearchParams();
      queryParams.set('tag', tag);
      navigate({
        pathname: location.pathname,
        search: queryParams.toString()
      });
    } else {
      // Remove query parameter when selecting 'All'
      navigate({
        pathname: location.pathname,
        search: ''
      });
    }
  };

  useEffect(() => {
    if (loading) return; // Don't run observer setup while loading

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1, // Trigger when 10% of the element is visible
    };

    const observerCallback = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target); // Stop observing once animated
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Select cards that need animation. We target them directly.
    const cardsToObserve = document.querySelectorAll('.product-card.scroll-animate:not(.is-visible)');
    
    cardsToObserve.forEach(card => {
      observer.observe(card);
    });

    return () => {
      cardsToObserve.forEach(card => {
        observer.unobserve(card);
      });
      observer.disconnect();
    };
  }, [filteredProducts, loading]); // Re-run when filteredProducts or loading state changes

  return (
    <div className="products-page">
      <a href="#product-filters" className="skip-link">Skip to product filters</a>
      <a href="#products-grid" className="skip-link">Skip to products</a>
      <PageHeader
        title="Our Products"
        description="Explore our selection of handcrafted, homemade baked goods. Each item is made with care and attention to detail."
      />
      <div className="container">
        {loading ? (
          <div className="loading-container" aria-live="polite" aria-busy="true">
            <div className="loading-spinner" role="progressbar" aria-label="Loading products"></div>
            <p>Loading products...</p>
          </div>
        ) : (
          <>
            <div id="product-filters" className="tag-filter" role="group" aria-label="Filter products by category">
              <button 
                className={`tag-button ${activeTag === null ? 'active' : ''}`}
                onClick={() => handleTagSelection(null)}
                aria-pressed={activeTag === null}
                aria-label="Show all products"
              >
                All
              </button>
              {allTags.map(tag => {
                const tagName = tag.charAt(0).toUpperCase() + tag.slice(1);
                return (
                  <button
                    key={tag}
                    className={`tag-button ${activeTag === tag ? 'active' : ''}`}
                    onClick={() => handleTagSelection(tag)}
                    aria-pressed={activeTag === tag}
                    aria-label={`Show only ${tagName} products`}
                  >
                    {tagName}
                  </button>
                );
              })}
            </div>

            {filteredProducts.length === 0 ? (
              <div className="no-products">
                <p>No products found in this category.</p>
              </div>
            ) : (
              <div id="products-grid" className="products-grid" role="list" aria-label="Products grid" data-testid="products-grid">
                {filteredProducts.map(product => (
                  <article 
                    className="product-card scroll-animate fade-in-up-init" 
                    key={product.slug}
                    role="listitem"
                    tabIndex={0}
                  >
                    <img 
                      src={product.image} 
                      alt={`${product.title}`} 
                      className="product-image"
                    />
                    <div className="product-content">
                      <h2 id={`product-title-${product.slug}`}>{product.title}</h2>
                      <p>{product.description ? product.description.split('\n')[0] : 'No description available'}</p>
                      
                      {product.allergens && product.allergens.length > 0 && (
                        <div className="allergens" aria-labelledby={`allergens-heading-${product.slug}`}>
                          <h3 id={`allergens-heading-${product.slug}`}>Contains:</h3>
                          <ul className="allergen-list">
                            {product.allergens.map(allergen => (
                              <li key={allergen} className="allergen-tag">{allergen}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      <div className="product-tags" aria-label="Product categories">
                        {product.tags && product.tags.map(tag => (
                          <span key={tag} className="tag">{tag}</span>
                        ))}
                      </div>
                      
                      <Link 
                        to={`/products/${product.slug}`} 
                        className="view-product-btn"
                        aria-labelledby={`product-title-${product.slug}`}
                      >
                        View Details
                        <span className="sr-only"> of {product.title}</span>
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
